/*
 * Confidential and Proprietary.
 * Do not distribute without Bloom Harbor Gifts consent.
 * Copyright Bloom Harbor Gifts 2019. All rights reserved.
 */

import React, { useContext, useState } from 'react';
import {
    object, string, shape, bool,
} from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import AppBar from '@material-ui/core/AppBar';
import Card from '@material-ui/core/Card';
import ReactMarkdown from 'react-markdown';
import ReactHtmlParser from 'react-html-parser';
import { useSelector } from 'react-redux';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useMediaQuery } from '@material-ui/core';
import GraphqlWineDisclaimer from './GraphqlWineDisclaimer/WineDisclaimerContainer';
import { htmlToText } from '../../../../helpers/common/helper';
import replaceFontWeight from '../../../../helpers/replaceFontWeight';
import replaceSpecialChar from '../../../../helpers/replaceSpecialChar';
import { CrossMerch } from '../../CrossMerchContainer/CrossMerchContainer';

const styles = (theme) => ({
    root: {
        marginBottom: '40px',
        background: theme.palette.white,
        [theme.breakpoints.down('600')]: {
            marginBottom: '0',
        },
    },
    appBar: {
        width: '100%',
        color: theme.palette.cms?.primary || theme.palette.colorPrimary,
        margin: '0 auto',
        padding: '15px 0',
        background: theme.palette.white,
        borderBottom: `2px solid ${theme.palette.cms?.primary || theme.palette.colorPrimary}`,
    },
    heading: {
        fontSize: '20px',
        textTransform: 'capitalize',
        fontWeight: '500',
        textAlign: 'center',
    },

    descriptionBlock: {
        height: '100%',
        display: 'inline-block',
        padding: '0 15px',
        fontSize: '15px',
        '& ul': {
            margin: '10px 0px',
            padding: 'revert',
        },
    },
    subHeading: {
        display: 'flex',
        margin: '15px 0px',
        '& h2': {
            fontSize: '16px',
            margin: '0',
        },
        '& span': {
            margin: '0 0 0 5px',
        },
    },
    shortDescription: {
        display: 'block',
        padding: `0px ${theme.spacing(2)}px`,
        boxSizing: 'border-box',
        textAlign: 'center',
    },
    longDescription: {
        display: 'block',
        [theme.breakpoints.down('sm')]: {
            display: 'none',
        },
    },
    longDescriptionMobile: {
        display: 'none',
        [theme.breakpoints.down('sm')]: {
            display: 'block',
        },
    },
    hidden: {
        display: 'none',
    },
    toggleDescription: {
        margin: '0 0 20px 0',
        padding: '0',
        color: theme.palette.cms?.primary || '#5e3987',
        fontSize: '14px',
        fontWeight: '700',
        textDecoration: 'underline',
        border: 'none',
        cursor: 'pointer',
    },
    mobileShortDes: {
        display: 'inline',
        '& p': {
            display: 'inline',
        },
    },
    promoContainer: {
        border: `1px solid ${theme.palette.cms?.promoMessageBorder || '#EDEDED'}`,
        padding: '18px',
        marginBottom: '10px',
        borderRadius: '3px',
        boxShadow: '0px 2px 2px rgba(125, 100, 100, 0.2)',
        opacity: 1,
    },
    promoMessage: {
        fontSize: '13px',
        textAlign: 'center',
        '& p': {
            margin: '0px',
        },
        '& a': {
            textDecoration: 'underline',
            color: theme.palette.cms?.black || '#000',
        },
    },
    wrapper: {
        background: '#fafafa',
        border: '0px',
        padding: '16px 16px 32px',
        borderRadius: '3px',
        marginBottom: '0px',
        [theme.breakpoints.up('600')]: {
            padding: '25px 24px 40px',
        },
        '& $appBar': {
            border: '0px',
            color: '#000000',
            background: 'transparent',
            padding: '15px 0px 10px',
        },
        '& $heading': {
            fontSize: '20px',
            textAlign: 'left',
            fontWeight: '700',
            textTransform: 'capitalize',
            lineHeight: '30px',
            fontFamily: 'museo-sans, "Area Variable", LatoBold',
            [theme.breakpoints.up('600')]: {
                fontSize: '24px',
            },
        },
        '& $shortDescription': {
            padding: '12px 0px',
            textAlign: 'left',
            fontSize: '16px',
            lineHeight: '26px',
            [theme.breakpoints.up('600')]: {
                lineHeight: '28px',
            },
        },
        '& $subHeading': {
            margin: '24px 0px 16px',
            '& h2': {
                fontSize: '18px',
                fontFamily: 'museo-sans, "Area Variable", LatoBold',
                fontWeight: '700',
            },
            '& span': {
                fontSize: '18px',
            },
        },
        '& $longDescription': {
            padding: '0px',
            fontSize: '16px',
            lineHeight: '26px',
            [theme.breakpoints.down('sm')]: {
                display: 'block',
            },
        },
        '& $descriptionBlock': {
            padding: '0px',
        },
        '& $longDescriptionMobile': {
            [theme.breakpoints.down('sm')]: {
                display: 'none',
            },
        },
    },
    details: {
        textDecoration: 'underline',
        textDecorationThickness: '1px',
        textUnderlineOffset: '3px',
        fontSize: '16px',
        alignSelf: 'flex-start',
        cursor: 'pointer',
        letterSpacing: '0.5px',
        display: 'flex',
        alignItems: 'center',
        lineHeight: '24px',
        [theme.breakpoints.up('600')]: {
            fontSize: '18px',
        },
        '& svg': {
            margin: '6px 0px 0px 12px',
            [theme.breakpoints.up('600')]: {
                margin: '4px 0px 0px 13px',
            },
        },
    },
    pdpShortStyle: {
        marginBottom: '28px',
        '& $heading': {
            [theme.breakpoints.up('600')]: {
                fontSize: '28px',
                textAlign: 'center',
                lineHeight: '36px',
                maxWidth: '1000px',
                margin: '0 auto 14px',
            },
        },
        '& $shortDescription': {
            [theme.breakpoints.up('600')]: {
                fontSize: '18px',
                lineHeight: '32px',
                maxWidth: '1000px',
                margin: '0 auto',
            },
        },
        '& $subHeading': {
            [theme.breakpoints.up('600')]: {
                margin: '0px auto 16px',
                maxWidth: '1000px',
                alignItems: 'center',
                '& h2': {
                    fontSize: '24px',

                },
            },
        },
        '& $longDescription': {
            [theme.breakpoints.up('600')]: {
                fontSize: '18px',
                lineHeight: '32px',
                maxWidth: '1000px',
                margin: '0 auto',
            },
        },
        '& $descriptionBlock': {
            width: '100%',
        },
    },
    skuBaseDescription: {
        display: 'block',
    },
});

const  GraphqlProductDetailsUI = ({
    classes, partNumber, productName, isPdpRedesignEnabled, isPdpRedesignShortEnabled,
    longDescription, isPassportBundleProduct, presentationFamily, selectedSku, productBrand, shortDescription, isPlaRedesignEnabled,
}) => {
    const [showLongDescription, setShowLongDescription] = useState(false);
    const [showBundleLongDescription, setShowBundleLongDescription] = useState(false);
    const { featureFlags = {} } = useContext(CrossMerch);
    const selectedSkuBasedAltImages = featureFlags['has-alt-images-pim-sourced'] || false;

    const [open, close] = useState(false);
    const isMobile = useMediaQuery('(max-width: 600px)');
    const productContentsDescription = selectedSku?.longDescription || '';
    const productShortDescription = selectedSku?.shortDescription || '';
    const parseHtml = (content) => ReactHtmlParser(content, {
        transform(node) {
            if (node?.type === 'tag' && node?.name === 'a') {
                const nodesData = node;
                const newData = nodesData?.children?.some((getNode) => getNode?.children?.some((getNode1) => getNode1?.data === 'click here')
                );
                const nodeDataFind = nodesData?.children?.some(
                    (getNode) => getNode?.data === 'click here'
                );

                if (nodesData && (newData || nodeDataFind)) {
                    nodesData.attribs['aria-label'] = 'click here';
                }
            }
            replaceFontWeight(node);
        },
    });
    const detailToggle = () => {
        close(!open);
    };

    let pdpReskinStyle = '';
    if (isPdpRedesignShortEnabled || isPlaRedesignEnabled) {
        pdpReskinStyle = `${classes.wrapper} ${classes.pdpShortStyle}`;
    } else if (isPdpRedesignEnabled) {
        pdpReskinStyle = classes.wrapper;
    }
    // Get first eight words from description
    let shortDes = longDescription?.split(' ').slice(0, 8).join(' ');
    if (longDescription) shortDes = htmlToText(longDescription)?.split(' ').slice(0, 8).join(' ');
    const descriptionBlock = () => (
        <div className={classes.descriptionBlock}>
            {presentationFamily !== 'food' && (
                <div className={classes.subHeading}>
                    <h2>Product Details:</h2> <span>{partNumber}</span>
                </div>
            )}
            <div>
                {/**
                     * @intention
                     * - Desktop: Always visible
                     * - Mobile: Hidden by default. Toggle visibility (read more/less)
                     */}
                <div className={classes.longDescription}>
                    {longDescription && parseHtml(longDescription)}
                    {selectedSkuBasedAltImages && productContentsDescription && <div className={classes.skuBaseDescription}>{parseHtml(productContentsDescription)}</div>}
                    {selectedSku?.ageVerifyFlag === 'true' && productBrand === 'harryanddavid' // showing wine disclaimer only for HD brand products
                        && (
                            <>
                                <GraphqlWineDisclaimer />
                            </>
                        )}
                </div>
                {showLongDescription && (
                    <div>
                        {ReactHtmlParser(longDescription, {
                            transform(node) {
                                replaceFontWeight(node);
                            },
                        })}
                        {selectedSkuBasedAltImages && productContentsDescription && (
                            <div>
                                {ReactHtmlParser(productContentsDescription, {
                                    transform(node) {
                                        replaceFontWeight(node);
                                    },
                                })}
                            </div>
                        )}
                        {selectedSku?.ageVerifyFlag === 'true' && productBrand === 'harryanddavid' && ( // showing wine disclaimer only for HD brand products
                            <>
                                <GraphqlWineDisclaimer />
                            </>
                        )}
                    </div>
                )}
                <div className={classes.longDescriptionMobile}>
                    {presentationFamily !== 'food' && !showLongDescription && (
                        <ReactMarkdown
                            className={classes.mobileShortDes}
                            source={`${shortDes}... `}
                            escapeHtml={false}
                        />
                    )}
                    <button
                        className={classes.toggleDescription}
                        type="button"
                        onClick={() => setShowLongDescription(!showLongDescription)}
                    >
                        {showLongDescription
                            ? 'Read less'
                            : 'Read more'}
                    </button>
                </div>
            </div>
        </div>
    );
    return (
        <>
            <Card className={`${classes.root} ${pdpReskinStyle}`} variant="outlined">
                <AppBar
                    className={`${classes.appBar}`}
                    position="static"
                    elevation={0}
                >
                    <div className={classes.heading}>
                        {isPdpRedesignEnabled
                            ? (
                                `About ${ReactHtmlParser(replaceSpecialChar(productName))}`

                            ) : 'Description' }
                    </div>
                    <div className={classes.shortDescription}>
                        {ReactHtmlParser(shortDescription, { transform(node) { replaceFontWeight(node); } })}
                        {selectedSkuBasedAltImages && productShortDescription && <div>{ReactHtmlParser(productShortDescription, { transform(node) { replaceFontWeight(node); } })}</div>}
                    </div>
                </AppBar>
                {(isPdpRedesignShortEnabled || isPlaRedesignEnabled) && !isMobile && isPdpRedesignEnabled ? <></> : (isPdpRedesignEnabled && <div role="presentation" onClick={() => detailToggle()} className={classes.details}>More details {open ? <ChevronUp color="#2F363D" width={24} height={24} /> : <ChevronDown color="#2F363D" width={24} height={24} />}</div>)}
                {isPdpRedesignEnabled && (open || (!isMobile && (isPdpRedesignShortEnabled || isPlaRedesignEnabled)))
                && (
                    descriptionBlock()
                ) }
                {!isPdpRedesignEnabled && descriptionBlock()}
            </Card>
            {isPassportBundleProduct?.details?.item_sku
            && (
                <Card className={classes.root} variant="outlined">
                    <div className={classes.descriptionBlock}>
                        <div className={classes.subHeading}>
                            <h2>Celebrations Passport Details</h2>: <span>{isPassportBundleProduct?.details?.item_sku}</span>
                        </div>
                        <div>
                            {/**
                             * @intention
                             * - Desktop: Always visible
                             * - Mobile: Hidden by default. Toggle visibility (read more/less)
                             */}
                            <div className={classes.longDescription}>
                                <ReactMarkdown source={isPassportBundleProduct?.details?.bundle_details?.long_description} escapeHtml={false} />
                            </div>
                            <div className={showBundleLongDescription ? '' : classes.hidden}><ReactMarkdown source={isPassportBundleProduct?.details?.bundle_details?.long_description} escapeHtml={false} />
                            </div>
                            <div className={classes.longDescriptionMobile}>
                                <button
                                    className={classes.toggleDescription}
                                    type="button"
                                    onClick={() => setShowBundleLongDescription(!showBundleLongDescription)}
                                >
                                    {showBundleLongDescription
                                        ? 'Read less'
                                        : 'Read more'}
                                </button>
                            </div>
                        </div>
                    </div>
                </Card>
            )}
        </>
    );
};

GraphqlProductDetailsUI.defaultProps = {
    isPassportBundleProduct: {},
    selectedSku: {},
    productName: '',
    productBrand: '',
    isPdpRedesignEnabled: false,
    isPdpRedesignShortEnabled: false,
    isPlaRedesignEnabled: false,
};

GraphqlProductDetailsUI.propTypes = {
    classes: object.isRequired,
    partNumber: string.isRequired,
    longDescription: string.isRequired,
    shortDescription: string.isRequired,
    isPassportBundleProduct: shape({
        enable: bool,
        prices: {},
        details: shape({
            bundle_details: shape({
                long_description: string,
            }),
        }),
    }),
    presentationFamily: string.isRequired,
    selectedSku: shape({
        ageVerifyFlag: string,
    }),
    productBrand: string,
    productName: string,
    isPdpRedesignEnabled: bool,
    isPdpRedesignShortEnabled: bool,
    isPlaRedesignEnabled: bool,
};

export default withStyles(styles)(GraphqlProductDetailsUI);
